'use client'

import * as React from 'react'
import { cn } from '../../lib/utils'
import { Button } from './button'
import { useIsRTLDirection } from '../../hooks'
import type { LucideIcon } from 'lucide-react'

export interface PageHeaderAction {
  label: string
  icon?: LucideIcon
  onClick: () => void
  variant?: 'default' | 'outline' | 'ghost'
  primary?: boolean
  disabled?: boolean
  testId?: string
}

export interface PageHeaderProps {
  /** Page title */
  title: string
  /** Optional subtitle/description */
  subtitle?: string
  /** Action buttons */
  actions?: PageHeaderAction[]
  /** Additional className */
  className?: string
  /**
   * @deprecated No longer needed - RTL is detected automatically from document direction
   */
  isRTL?: boolean
}

/**
 * Unified page header component
 * Displays title, subtitle, and action buttons consistently across all pages
 *
 * RTL Support: Handled by document.documentElement.dir set by I18nProvider.
 * Uses CSS logical properties (text-start, me-*) that automatically flip.
 * justify-between with dir="rtl" puts first child on right, last on left.
 * NO manual flex-row-reverse - that would double-reverse the layout!
 */
export function PageHeader({
  title,
  subtitle,
  actions = [],
  className,
}: PageHeaderProps) {
  const isRTL = useIsRTLDirection()

  return (
    <div className={cn(
      'flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
      className
    )}>
      {/* Title - text-start is a logical property that works with dir="rtl" */}
      <div className="min-w-0 text-start">
        <h2 className="text-2xl font-bold text-foreground tracking-tight break-words">{title}</h2>
        {subtitle && (
          <p className="text-muted-foreground mt-1 break-words">{subtitle}</p>
        )}
      </div>
      {actions.length > 0 && (
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end sm:gap-3">
          {actions.map((action, index) => {
            const Icon = action.icon
            const isPrimary = action.primary ?? (index === actions.length - 1)

            return (
              <Button
                key={index}
                variant={action.variant ?? (isPrimary ? 'default' : 'outline')}
                onClick={action.onClick}
                disabled={action.disabled}
                data-testid={action.testId}
                className={cn(
                  'w-full rounded-xl sm:w-auto',
                  isPrimary && cn(
                    'text-primary-foreground hover:opacity-90 shadow-lg shadow-primary/25 px-4 sm:px-6',
                    isRTL ? 'bg-gradient-to-l from-primary to-primary/90' : 'bg-gradient-to-r from-primary to-primary/90'
                  )
                )}
              >
                {/* me-2 = margin-end, which becomes margin-left in RTL */}
                {Icon && <Icon className="w-4 h-4 me-2" />}
                {action.label}
              </Button>
            )
          })}
        </div>
      )}
    </div>
  )
}
